import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, NavLink } from 'react-router-dom';
import { useAppStore } from '../../store/appStore';
import { useUserSession } from '../../providers/UserSessionProvider';
import './CollapsiblePanel.css';

// ── Icons ──────────────────────────────────────────────────────────
// One source of truth: the MERIDIAN icon module. Local aliases below map the
// nav's concept names (StationPin for the search sub-item, LineChart for the
// analytics trend, Availability at two sizes) onto the shared glyphs, and pin
// the sizes the rail relies on. Note: `.nav-icon svg` is CSS-sized to 18px, so
// top-level group icons ignore their intrinsic size; sub-item icons render at
// their 20px default.
import {
  Search as SearchIcon,
  StationPin as StationIcon,
  Health as HealthPulseIcon,
  Statistics as StatisticsIcon,
  Analytics as LineChartIcon,
  StationPin as StationPinIcon,
  Coordinate as CoordinateIcon,
  Country as CountryIcon,
  Info as InfoIcon,
  Availability as AvailabilityMiniIcon,
  Observation as ObservationIcon,
  QC as QCIcon,
  Analytics as AnalyticsIcon,
  ChevronDown,
  Hamburger as HamburgerIcon,
  ChevronLeft as ChevronLeftIcon,
  ThemeLight as SunIcon,
  ThemeDark as MoonIcon,
  ThemeSystem as SystemIcon,
  Logout as LogoutIcon,
} from '../icons';

/** Disclosure caret — carries the `.nav-chevron` hook the rail's CSS rotates/recolors. */
const ChevronDownIcon = () => <ChevronDown className="nav-chevron" size={14} />;

// ── Nav data ───────────────────────────────────────────────────────
// Three top-level groups: Search (methods), Station (station-scoped pages), and Analytics
// (network-wide health/analytics/QC). "Station" + "Availability" used to be top-level links;
// they now live under the Station group. "Stations" (network-wide station statistics) moved
// out of the old Network group into Station.
type SectionId = 'search' | 'station' | 'analytics';
type SearchChildId = 'station' | 'coordinates' | 'country';
type StationChildId = 'stations' | 'info' | 'availability' | 'observation';
type AnalyticsChildId = 'health' | 'analytics' | 'qc';
type NavChildId = SearchChildId | StationChildId | AnalyticsChildId;

interface NavChild {
  id: NavChildId;
  label: string;
  path: string;
  Icon: React.FC;
  /** Exact-match only. Set for paths that are prefixes of sibling routes (e.g. /network,
   *  /station) so react-router's NavLink doesn't mark them active on deeper URLs. */
  end?: boolean;
}

interface NavItem {
  id: SectionId;
  label: string;
  path?: string;
  Icon: React.FC;
  children?: NavChild[];
}

const navigationItems: NavItem[] = [
  {
    id: 'search',
    label: 'Search',
    Icon: SearchIcon,
    children: [
      { id: 'station', label: 'Station code', path: '/search/station', Icon: StationPinIcon },
      { id: 'coordinates', label: 'Location', path: '/search/coordinates', Icon: CoordinateIcon },
      { id: 'country', label: 'Country', path: '/search/country', Icon: CountryIcon },
    ],
  },
  {
    id: 'station',
    label: 'Station',
    Icon: StationIcon,
    children: [
      { id: 'stations', label: 'Overview', path: '/network/statistics', Icon: StatisticsIcon },
      { id: 'info', label: 'Info', path: '/station', Icon: InfoIcon, end: true },
      { id: 'availability', label: 'Availability', path: '/availability', Icon: AvailabilityMiniIcon },
      { id: 'observation', label: 'Observation', path: '/observations', Icon: ObservationIcon },
    ],
  },
  {
    id: 'analytics',
    label: 'Analytics',
    Icon: AnalyticsIcon,
    children: [
      { id: 'health', label: 'Health', path: '/network', Icon: HealthPulseIcon, end: true },
      { id: 'analytics', label: 'Station Analytics', path: '/network/analytics', Icon: LineChartIcon },
      { id: 'qc', label: 'QC', path: '/quality', Icon: QCIcon },
    ],
  },
];

type ThemePreference = 'light' | 'dark' | 'system';
const THEME_KEY = 'stationExplorer_theme';

function applyTheme(pref: ThemePreference) {
  const root = document.documentElement;
  if (pref === 'light') {
    root.setAttribute('data-theme', 'light');
    root.style.colorScheme = 'light';
  } else if (pref === 'dark') {
    root.setAttribute('data-theme', 'dark');
    root.style.colorScheme = 'dark';
  } else {
    root.removeAttribute('data-theme');
    root.style.colorScheme = '';
  }
}

/** Resolve which top-level section (and child, if any) is active for a URL.
 *  Order matters: more specific prefixes are tested before their parents, and the
 *  station-statistics routes (which live under /network/* but belong to the Station
 *  group) are matched before the generic /network catch-all. */
function resolveActive(pathname: string): { section: SectionId | null; childId: NavChildId | null } {
  // Quality lives under Analytics now. /station-quality is a legacy redirect to /quality
  // and must be checked BEFORE /station (prefix would otherwise swallow it).
  if (
    pathname === '/quality' || pathname.startsWith('/quality/') ||
    pathname === '/station-quality' || pathname.startsWith('/station-quality/')
  ) {
    return { section: 'analytics', childId: 'qc' };
  }
  // Station statistics table — under /network/* (and legacy /statistics) but grouped under Station.
  if (pathname.startsWith('/network/statistics') || pathname === '/statistics') {
    return { section: 'station', childId: 'stations' };
  }
  // Observations (map + per-station) → Station › Observation.
  if (pathname.startsWith('/observations') || pathname.match(/^\/station\/[^/]+\/observations/)) {
    return { section: 'station', childId: 'observation' };
  }
  if (pathname === '/station' || pathname.startsWith('/station/')) {
    return { section: 'station', childId: 'info' };
  }
  if (pathname.startsWith('/availability')) {
    return { section: 'station', childId: 'availability' };
  }
  if (pathname.startsWith('/search')) {
    // /search/:method → map the segment to a search child. Unknown/absent → default 'station'.
    const method = pathname.split('/')[2];
    const childId: SearchChildId =
      method === 'coordinates' ? 'coordinates' : method === 'country' ? 'country' : 'station';
    return { section: 'search', childId };
  }
  if (pathname.startsWith('/network/analytics')) return { section: 'analytics', childId: 'analytics' };
  if (pathname.startsWith('/network')) return { section: 'analytics', childId: 'health' };
  return { section: null, childId: null };
}

// ── NavGroup ───────────────────────────────────────────────────────
interface NavGroupProps {
  item: NavItem & { children: NavChild[] };
  collapsed: boolean;
  activeChildId: NavChildId | null;
  isSectionActive: boolean;
  /** The single collapsed-flyout currently open across ALL groups (lifted to the
   *  panel). Only one may be open at a time so switching from one icon to another
   *  is an instant, clean hand-off — no 300ms overlap where two flyouts co-exist
   *  and the old one then vanishes abruptly. */
  openFlyoutId: SectionId | null;
  setOpenFlyoutId: React.Dispatch<React.SetStateAction<SectionId | null>>;
}

const NavGroup = ({
  item,
  collapsed,
  activeChildId,
  isSectionActive,
  openFlyoutId,
  setOpenFlyoutId,
}: NavGroupProps) => {
  const [open, setOpen] = useState(isSectionActive);
  // This group's flyout is open iff it's the one the panel has selected. All the
  // existing call sites keep using flyoutOpen / setFlyoutOpen unchanged; the shim
  // below routes them through the shared state (functional updates included), so
  // opening THIS flyout implicitly closes any other that was open.
  const flyoutOpen = openFlyoutId === item.id;
  const setFlyoutOpen = useCallback(
    (value: boolean | ((prev: boolean) => boolean)) => {
      setOpenFlyoutId((prev) => {
        const next = typeof value === 'function' ? value(prev === item.id) : value;
        return next ? item.id : prev === item.id ? null : prev;
      });
    },
    [item.id, setOpenFlyoutId]
  );
  // Screen-space anchor for the portaled collapsed flyout. The flyout is rendered
  // into document.body (see below) so it escapes the rail's `overflow: hidden` and
  // the search page's z-index stack; position:fixed needs the trigger's viewport rect.
  const [flyoutPos, setFlyoutPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const submenuRef = useRef<HTMLUListElement | null>(null);
  const flyoutRef = useRef<HTMLDivElement | null>(null);
  const flyoutCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();

  // Auto-open the disclosure when a child route is active (expanded sidebar only).
  useEffect(() => {
    if (isSectionActive && !collapsed) setOpen(true);
  }, [isSectionActive, collapsed]);

  // Close flyout when sidebar expands, close inline when it collapses.
  useEffect(() => {
    if (collapsed) setOpen(false);
    else setFlyoutOpen(false);
  }, [collapsed]);

  // Close flyout on route change (child link clicked).
  useEffect(() => {
    setFlyoutOpen(false);
  }, [location.pathname]);

  // Measure the trigger's viewport rect whenever the collapsed flyout opens, and
  // keep it aligned if the user scrolls/resizes while it's open. Feeds the
  // position:fixed portal so the popover sits just to the right of the rail icon.
  useEffect(() => {
    if (!flyoutOpen || !collapsed) {
      setFlyoutPos(null);
      return;
    }
    const measure = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (rect) setFlyoutPos({ top: rect.top, left: rect.right + 4 });
    };
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [flyoutOpen, collapsed]);

  // Outside-click closes flyout.
  useEffect(() => {
    if (!flyoutOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        flyoutRef.current && !flyoutRef.current.contains(target)
      ) {
        setFlyoutOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [flyoutOpen]);

  const focusFirstChild = useCallback(() => {
    const container = collapsed ? flyoutRef.current : submenuRef.current;
    const first = container?.querySelector<HTMLAnchorElement>('a');
    first?.focus();
  }, [collapsed]);

  const focusLastChild = useCallback(() => {
    const container = collapsed ? flyoutRef.current : submenuRef.current;
    const anchors = container?.querySelectorAll<HTMLAnchorElement>('a');
    if (anchors && anchors.length > 0) anchors[anchors.length - 1].focus();
  }, [collapsed]);

  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'Enter':
      case ' ': {
        e.preventDefault();
        if (collapsed) setFlyoutOpen((v) => !v);
        else setOpen((v) => !v);
        break;
      }
      case 'ArrowDown': {
        e.preventDefault();
        if (collapsed) setFlyoutOpen(true);
        else setOpen(true);
        requestAnimationFrame(focusFirstChild);
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        if (collapsed) setFlyoutOpen(true);
        else setOpen(true);
        requestAnimationFrame(focusLastChild);
        break;
      }
      case 'ArrowRight': {
        e.preventDefault();
        if (collapsed) setFlyoutOpen(true);
        else setOpen(true);
        break;
      }
      case 'Escape': {
        setOpen(false);
        setFlyoutOpen(false);
        break;
      }
    }
  };

  const handleChildKeyDown = (e: React.KeyboardEvent<HTMLUListElement | HTMLDivElement>) => {
    const container = collapsed ? flyoutRef.current : submenuRef.current;
    if (!container) return;
    const anchors = Array.from(container.querySelectorAll<HTMLAnchorElement>('a'));
    const activeIdx = anchors.findIndex((a) => a === document.activeElement);
    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        const next = anchors[(activeIdx + 1) % anchors.length];
        next?.focus();
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prev = anchors[(activeIdx - 1 + anchors.length) % anchors.length];
        prev?.focus();
        break;
      }
      case 'Home': {
        e.preventDefault();
        anchors[0]?.focus();
        break;
      }
      case 'End': {
        e.preventDefault();
        anchors[anchors.length - 1]?.focus();
        break;
      }
      case 'Escape':
      case 'ArrowLeft': {
        e.preventDefault();
        setOpen(false);
        setFlyoutOpen(false);
        triggerRef.current?.focus();
        break;
      }
    }
  };

  const clearCloseTimer = () => {
    if (flyoutCloseTimer.current) {
      clearTimeout(flyoutCloseTimer.current);
      flyoutCloseTimer.current = null;
    }
  };

  const scheduleClose = () => {
    clearCloseTimer();
    flyoutCloseTimer.current = setTimeout(() => setFlyoutOpen(false), 300);
  };

  const submenuId = `nav-${item.id}-submenu`;
  const parentClass = [
    'nav-item',
    'nav-group-trigger',
    'no-tooltip',
    collapsed ? '' : open ? 'is-open' : '',
    isSectionActive ? (collapsed ? 'active' : 'parent-active') : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className="nav-group"
      onMouseEnter={collapsed ? () => { clearCloseTimer(); setFlyoutOpen(true); } : undefined}
      onMouseLeave={collapsed ? scheduleClose : undefined}
    >
      <button
        ref={triggerRef}
        type="button"
        className={parentClass}
        onClick={() => {
          if (collapsed) setFlyoutOpen((v) => !v);
          else setOpen((v) => !v);
        }}
        onKeyDown={handleTriggerKeyDown}
        aria-expanded={collapsed ? flyoutOpen : open}
        aria-controls={submenuId}
        aria-current={isSectionActive && !collapsed ? 'location' : undefined}
        aria-label={item.label}
        data-tooltip={item.label}
      >
        <span className="nav-icon">
          <item.Icon />
        </span>
        {!collapsed && (
          <>
            <span className="nav-label">{item.label}</span>
            <ChevronDownIcon />
          </>
        )}
      </button>

      {/* Expanded: inline disclosure — outer <div> is the grid container so the
          grid-template-rows: 0fr→1fr animation collapses ALL children together.
          Nesting the <ul> as a single grid child was the fix for a bug where the
          first list item disappeared and the rest stayed visible. */}
      {!collapsed && (
        <div
          id={submenuId}
          className={`nav-group-submenu inline${open ? ' is-open' : ''}`}
        >
          <ul
            ref={submenuRef}
            role="group"
            aria-label={`${item.label} sections`}
            className="nav-group-submenu-inner"
            onKeyDown={handleChildKeyDown}
          >
            {item.children.map((child) => (
              <li key={child.id}>
                <NavLink
                  to={child.path}
                  end={child.end}
                  className={({ isActive }) =>
                    `nav-subitem${isActive || activeChildId === child.id ? ' active' : ''}`
                  }
                  aria-current={activeChildId === child.id ? 'page' : undefined}
                >
                  <span className="nav-subitem-icon">
                    <child.Icon />
                  </span>
                  <span>{child.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Collapsed: right-anchored flyout popover.
          Portaled to <body> so it escapes the rail's `overflow: hidden` (and the
          nested page-layout/sidebar overflow) that would otherwise clip it, and so
          it out-stacks the search page's floating chrome. Positioned with
          position:fixed at the trigger's viewport rect (see the measure effect). */}
      {collapsed && flyoutOpen && flyoutPos && createPortal(
        <div
          ref={flyoutRef}
          id={submenuId}
          role="group"
          aria-label={`${item.label} sections`}
          className="nav-group-submenu flyout flyout--portal"
          style={{ top: flyoutPos.top, left: flyoutPos.left }}
          onMouseEnter={clearCloseTimer}
          onMouseLeave={scheduleClose}
          onKeyDown={handleChildKeyDown}
        >
          <div className="nav-group-flyout-header">{item.label}</div>
          {item.children.map((child) => (
            <NavLink
              key={child.id}
              to={child.path}
              end={child.end}
              className={({ isActive }) =>
                `nav-subitem${isActive || activeChildId === child.id ? ' active' : ''}`
              }
              aria-current={activeChildId === child.id ? 'page' : undefined}
              onClick={() => setFlyoutOpen(false)}
            >
              <span className="nav-subitem-icon">
                <child.Icon />
              </span>
              <span>{child.label}</span>
            </NavLink>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

// ── Panel ──────────────────────────────────────────────────────────
export const CollapsiblePanel = () => {
  const location = useLocation();
  const { isPanelCollapsed, togglePanel } = useAppStore();
  const { userEmail, logout } = useUserSession();
  const { section, childId } = resolveActive(location.pathname);
  // Which collapsed-flyout is open, lifted out of the individual groups so only one
  // is ever mounted — moving between rail icons is a clean single hand-off.
  const [openFlyoutId, setOpenFlyoutId] = useState<SectionId | null>(null);
  const [theme, setTheme] = useState<ThemePreference>(() => {
    return (localStorage.getItem(THEME_KEY) as ThemePreference) || 'system';
  });

  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  // Every top-level item is now a collapsible group (Search / Station / Analytics) whose
  // children navigate via NavLinks — there are no simple top-level links left.
  return (
    <div className={`collapsible-panel ${isPanelCollapsed ? 'collapsed' : ''}`}>
      <button
        className="panel-toggle-btn"
        onClick={togglePanel}
        aria-label={isPanelCollapsed ? 'Open menu' : 'Close menu'}
      >
        {isPanelCollapsed ? <HamburgerIcon /> : <ChevronLeftIcon />}
      </button>

      <nav className="panel-nav" aria-label="Primary">
        {navigationItems.map((item) => {
          const isActive = section === item.id;
          return (
            <NavGroup
              key={item.id}
              item={item as NavItem & { children: NavChild[] }}
              collapsed={isPanelCollapsed}
              activeChildId={isActive ? childId : null}
              isSectionActive={isActive}
              openFlyoutId={openFlyoutId}
              setOpenFlyoutId={setOpenFlyoutId}
            />
          );
        })}
      </nav>

      <div className="panel-profile">
        {!isPanelCollapsed && (
          <div className="profile-theme-row">
            <div className="theme-switcher" role="radiogroup" aria-label="Theme preference">
              <button
                className={`theme-btn ${theme === 'light' ? 'active' : ''}`}
                onClick={() => setTheme('light')}
                aria-label="Light mode"
                role="radio"
                aria-checked={theme === 'light'}
              >
                <SunIcon />
              </button>
              <button
                className={`theme-btn ${theme === 'dark' ? 'active' : ''}`}
                onClick={() => setTheme('dark')}
                aria-label="Dark mode"
                role="radio"
                aria-checked={theme === 'dark'}
              >
                <MoonIcon />
              </button>
              <button
                className={`theme-btn ${theme === 'system' ? 'active' : ''}`}
                onClick={() => setTheme('system')}
                aria-label="System preference"
                role="radio"
                aria-checked={theme === 'system'}
              >
                <SystemIcon />
              </button>
            </div>
          </div>
        )}

        {!isPanelCollapsed && (
          <button className="profile-signout" onClick={logout} aria-label="Sign out">
            <LogoutIcon />
            <span>Sign out</span>
          </button>
        )}

        <div className="profile-user">
          <span className="profile-avatar">
            {userEmail ? userEmail.charAt(0).toUpperCase() : '?'}
          </span>
          {!isPanelCollapsed && (
            <span className="profile-email">{userEmail || 'Not signed in'}</span>
          )}
        </div>

        {isPanelCollapsed && (
          <button
            className="profile-collapsed-btn"
            onClick={() => {
              const next: ThemePreference = theme === 'system' ? 'light' : theme === 'light' ? 'dark' : 'system';
              setTheme(next);
            }}
            aria-label={`Theme: ${theme}. Click to change.`}
          >
            {theme === 'light' ? <SunIcon /> : theme === 'dark' ? <MoonIcon /> : <SystemIcon />}
          </button>
        )}
      </div>
    </div>
  );
};
